// components/Avatar/Base.tsx
import React, { forwardRef, useState } from "react";
import { Image, StyleSheet, StyleProp, View, ViewProps, ViewStyle } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";

export type AvatarBaseProps = ViewProps & {
  style?: StyleProp<ViewStyle>;
  size?: number; // square; falls back to style or 36
  initials: string; // already computed
  uri?: string | null; // final image url (optional)
  fontSize?: number; // defaults to 50% of size
  backgroundColor?: string; // fallback circle color
  textColor?: string; // initials color (default white)
  onImageError?: () => void;
};

export const AvatarBase = forwardRef<View, AvatarBaseProps>(function AvatarBase(
  { style, size, initials, uri, fontSize, backgroundColor = "#6B7280", textColor = "#fff", onImageError, ...rest },
  ref,
) {
  const { colors } = useTheme();
  const flat = StyleSheet.flatten(style) || {};
  const w = size ?? (typeof flat.width === "number" ? flat.width : 36);
  const h = size ?? (typeof flat.height === "number" ? flat.height : 36);
  const dim = Math.min(Number(w), Number(h));
  const fs = fontSize ?? Math.round(dim * 0.5);
  const [errored, setErrored] = useState(false);

  const showInitials = !uri || errored;

  return (
    <View
      ref={ref}
      {...rest}
      style={[styles.container, { width: dim, height: dim, borderRadius: dim / 2, borderColor: colors.border }, style]}
    >
      {showInitials ? (
        <View style={[styles.fallback, { backgroundColor, borderRadius: dim / 2 }]}>
          <ThemedText style={{ color: textColor, fontSize: fs }}>{initials || " "}</ThemedText>
        </View>
      ) : (
        <Image
          source={{ uri: uri! }}
          resizeMode="cover"
          onError={() => {
            setErrored(true);
            onImageError?.();
          }}
          style={{ width: "100%", height: "100%", borderRadius: dim / 2 }}
        />
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    borderWidth: StyleSheet.hairlineWidth,
    overflow: "hidden",
    justifyContent: "center",
    alignItems: "center",
  },
  fallback: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
});
